// AllManufacturerProductsRow Component
import React, { useState, useCallback, useEffect } from 'react';
import { Link20Regular, Pen20Regular, Delete20Regular } from '@fluentui/react-icons';
import { GenerateButton, HoverTip } from '@repo/ui';
import { MessageBar, MessageBarIntent } from '@fluentui/react-components';
import styled from 'styled-components';
import { useUserStore } from '@repo/front-end-state';
import Config from '../../common/Config';

interface AllManufacturerProductsRowProps {
  enabled: boolean;
  reason: string;
  feedback?: { intent: MessageBarIntent; message: string }[];
  addFeedback?: (feedback: { intent: MessageBarIntent; message: string }, clear?: boolean) => void;
  removeFeedback?: (index: number) => void;
  manufacturerProductsSheetUrl?: string;
  onSheetUrlUpdate?: (url: string) => void;
}

const AllManufacturerProductsRow_: React.FC<AllManufacturerProductsRowProps> = ({
  enabled,
  reason,
  feedback,
  addFeedback,
  removeFeedback,
  manufacturerProductsSheetUrl,
  onSheetUrlUpdate,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [staticSheetUrl, setStaticSheetUrl] = useState<string | null>(null);
  const [isLoadingStaticUrl, setIsLoadingStaticUrl] = useState(false);
  const { authMetadata } = useUserStore();

  // Helper function for future use - extracting sheet ID from URL
  // const getSheetId = (url?: string) => {
  //   if (!url) return undefined;
  //   const match = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
  //   return match ? match[1] : undefined;
  // };
  // const sheetId = getSheetId(manufacturerProductsSheetUrl);

  // Fetch static sheet URL on component mount
  useEffect(() => {
    const fetchStaticSheetUrl = async () => {
      const accessToken: string | undefined = authMetadata?.tokens?.accessToken;

      if (!accessToken) {
        return;
      }

      setIsLoadingStaticUrl(true);

      try {
        const response = await fetch(
          `${Config.middlewareApi}api/google-sheets/manufacturer-products-sheet-url`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        if (response.ok) {
          const result = await response.json();
          setStaticSheetUrl(result.sheet_url);
        } else {
          console.warn('Failed to fetch static sheet URL:', response.status);
        }
      } catch (error) {
        console.error('Error fetching static sheet URL:', error);
      } finally {
        setIsLoadingStaticUrl(false);
      }
    };

    fetchStaticSheetUrl();
  }, [authMetadata]);

  const generateManufacturerProductsSheet = useCallback(async () => {
    const accessToken: string | undefined = authMetadata?.tokens?.accessToken;

    if (!accessToken) {
      addFeedback?.({
        intent: 'error',
        message: 'Missing access token',
      });
      return;
    }

    setIsGenerating(true);
    addFeedback?.(
      {
        intent: 'success',
        message: 'Updating All Manufacturer Products sheet...',
      },
      true,
    );

    try {
      const response = await fetch(
        `${Config.middlewareApi}api/google-sheets/export-manufacturer-products`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;

        // Provide more specific error messages based on status code
        switch (response.status) {
          case 400:
            errorMessage = 'Invalid request. Please try again.';
            break;
          case 401:
            errorMessage = 'Authentication failed. Please log in again.';
            break;
          case 403:
            errorMessage = 'You do not have permission to generate this sheet.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = `Failed to generate sheet (${response.status}). Please try again.`;
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();

      addFeedback?.({
        intent: 'success',
        message: `All Manufacturer Products sheet updated successfully! ${result.products_count} products exported.`,
      });

      // Update the sheet URL
      if (onSheetUrlUpdate && result.sheet_url) {
        onSheetUrlUpdate(result.sheet_url);
      }

      // Update the static sheet URL since we just generated it
      if (result.sheet_url) {
        setStaticSheetUrl(result.sheet_url);
      }

      // Open the Google Sheet in a new tab
      if (result.sheet_url) {
        window.open(result.sheet_url, '_blank');
      }

      // Exit edit mode
      setIsEditMode(false);
    } catch (error) {
      console.error('Error generating All Manufacturer Products sheet:', error);
      addFeedback?.({
        intent: 'error',
        message: 'Failed to generate All Manufacturer Products sheet. Please try again.',
      });
    } finally {
      setIsGenerating(false);
    }
  }, [authMetadata, addFeedback, onSheetUrlUpdate]);

  const handleGenerate = () => {
    generateManufacturerProductsSheet();
  };

  const handleCancel = () => {
    setIsEditMode(false);
  };

  // Determine which URL to display - prioritize the static sheet URL
  const displayUrl = staticSheetUrl || manufacturerProductsSheetUrl;
  const isLoading = isLoadingStaticUrl;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
        {displayUrl ? (
          <>
            <Link20Regular style={{ color: '#00727D', flexShrink: 0 }} />
            <div
              style={{ position: 'relative', flex: 1, marginRight: 8, maxWidth: 350, minWidth: 0 }}
            >
              <HoverTip text={displayUrl} display="block" maxWidth={340}>
                <a
                  href={displayUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open All Manufacturer Products sheet in new tab"
                  style={{
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: '#00727D',
                    textDecoration: 'underline',
                    fontSize: '14px',
                    maxWidth: '100%',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  Manufacturer Products
                </a>
              </HoverTip>
            </div>
          </>
        ) : (
          <span style={{ flex: 1, color: '#888', fontSize: '16px', marginRight: 8 }}>
            {isLoading ? 'Loading...' : 'No manufacturer products sheet available'}
          </span>
        )}

        <div style={{ flexShrink: 0, display: 'flex', gap: 8, alignItems: 'center' }}>
          {!isEditMode ? (
            <button
              className="btnPen"
              type="button"
              onClick={() => setIsEditMode(true)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Pen20Regular />
            </button>
          ) : (
            <>
              <GenerateButton
                enabled={enabled && !isGenerating}
                reason={isGenerating ? 'Generating sheet...' : reason}
                onClick={handleGenerate}
                enabledTooltipText="Generate all manufacturer data"
              />
              <button
                className="btnDelete"
                type="button"
                onClick={handleCancel}
                disabled={isGenerating}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: isGenerating ? 'not-allowed' : 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: isGenerating ? 0.5 : 1,
                }}
              >
                <Delete20Regular />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Feedback messages below the row */}
      {feedback && feedback.length > 0 && (
        <div className="inline-feedback" style={{ marginTop: '8px' }}>
          {feedback.map((feedbackItem, i) => (
            <MessageBar key={i} intent={feedbackItem.intent}>
              {feedbackItem.message}
              {removeFeedback && (
                <button
                  onClick={() => removeFeedback(i)}
                  style={{
                    background: 'none',
                    cursor: 'pointer',
                    height: '16px',
                  }}
                >
                  ×
                </button>
              )}
            </MessageBar>
          ))}
        </div>
      )}
    </>
  );
};

export const AllManufacturerProductsRow = styled(AllManufacturerProductsRow_)`
  width: 100%;
`;
